<template>
  <div>
    <p v-if="labelText.length > 0" class="label cursor-pointer" :style="labelStyleObject">
      <span class="label-text">
        {{ labelText }}<span v-if="required === true" class="text-error inline-block mx-1">*</span>
      </span>
    </p>
    <label class="input input-bordered flex items-center space-x-2" :class="wrapperComputedClass">
      <slot v-if="hasIcon === true" name="icon"></slot>
      <input
        :id="idValue"
        :name="nameValue"
        :type="typeValue"
        :value="modelValueString"
        class="grow"
        :placeholder="placeholderValue"
        :maxlength="maxLengthValue"
        :minlength="minLengthValue"
        :pattern="patternValue"
        :autocomplete="autocompleteValue"
        :inputmode="inputModeValue"
        :spellcheck="spellcheckValue"
        :autocapitalize="autocapitalizeValue"
        :disabled="disabled === true"
        :readonly="readonly === true"
        :aria-required="required === true ? 'true' : 'false'"
        :aria-invalid="error === true ? 'true' : 'false'"
        :aria-describedby="describedByValue"
        @input="onInput"
        @keydown="$emit('keydown', $event)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />
    </label>
    <div v-if="showCharCount === true" class="text-xs text-right pr-1 mt-1 text-gray-500">
      {{ charCountText }}
    </div>
    <p v-if="error === true && errorMessageText.length > 0" :id="errorIdValue" class="text-error text-xs mt-1">
      {{ errorMessageText }}
    </p>
    <p v-if="hintText.length > 0" :id="hintIdValue" class="text-xs mt-1 text-gray-500">
      {{ hintText }}
    </p>
  </div>
</template>

<script setup>
import { computed, useSlots } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  id: { type: String, default: '' },
  name: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  type: { type: String, default: 'text' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  hint: { type: String, default: '' },
  maxLength: { type: Number, default: 100 },
  minLength: { type: Number, default: 0 },
  pattern: { type: String, default: '' },
  autocomplete: { type: String, default: 'off' },
  inputMode: { type: String, default: '' },
  spellcheck: { type: Boolean, default: false },
  autocapitalize: { type: String, default: 'none' },
  showCharCount: { type: Boolean, default: true },
  labelStyle: { type: Object, default: null },
  wrapperClass: { type: String, default: 'w-full' },
  sanitizeMode: { type: String, default: 'text' }
});

const emit = defineEmits(['update:modelValue', 'keydown', 'blur', 'focus', 'input']);

const slots = useSlots();

const hasIcon = computed(() => {
  return slots.icon !== undefined;
});

const modelValueString = computed(() => {
  if (typeof props.modelValue === 'number') {
    return String(props.modelValue);
  }
  if (typeof props.modelValue === 'string') {
    return props.modelValue;
  }
  return '';
});

const idValue = computed(() => {
  return props.id;
});

const nameValue = computed(() => {
  return props.name;
});

const typeValue = computed(() => {
  return props.type;
});

const labelText = computed(() => {
  return typeof props.label === 'string' ? props.label : '';
});

const placeholderValue = computed(() => {
  return typeof props.placeholder === 'string' ? props.placeholder : '';
});

const hintText = computed(() => {
  return typeof props.hint === 'string' ? props.hint : '';
});

const errorMessageText = computed(() => {
  return typeof props.errorMessage === 'string' ? props.errorMessage : '';
});

const maxLengthValue = computed(() => {
  return props.maxLength > 0 ? props.maxLength : 0;
});

const minLengthValue = computed(() => {
  return props.minLength > 0 ? props.minLength : undefined;
});

const patternValue = computed(() => {
  return typeof props.pattern === 'string' && props.pattern.length > 0 ? props.pattern : undefined;
});

const autocompleteValue = computed(() => {
  return typeof props.autocomplete === 'string' && props.autocomplete.length > 0 ? props.autocomplete : 'off';
});

const inputModeValue = computed(() => {
  return typeof props.inputMode === 'string' && props.inputMode.length > 0 ? props.inputMode : undefined;
});

const spellcheckValue = computed(() => {
  return props.spellcheck === true ? 'true' : 'false';
});

const autocapitalizeValue = computed(() => {
  return typeof props.autocapitalize === 'string' && props.autocapitalize.length > 0 ? props.autocapitalize : 'none';
});

const wrapperComputedClass = computed(() => {
  const base = props.error === true ? 'input-error' : '';
  const extra = typeof props.wrapperClass === 'string' ? props.wrapperClass : '';
  return [base, extra].filter(v => typeof v === 'string' && v.length > 0).join(' ');
});

const labelStyleObject = computed(() => {
  if (props.labelStyle !== null && props.labelStyle !== undefined) {
    return props.labelStyle;
  }
  return undefined;
});

const errorIdValue = computed(() => {
  if (typeof props.id === 'string' && props.id.length > 0) {
    return props.id + '-error';
  }
  return '';
});

const hintIdValue = computed(() => {
  if (typeof props.id === 'string' && props.id.length > 0) {
    return props.id + '-hint';
  }
  return '';
});

const describedByValue = computed(() => {
  const ids = [];
  if (props.error === true && errorIdValue.value.length > 0) {
    ids.push(errorIdValue.value);
  }
  if (hintText.value.length > 0 && hintIdValue.value.length > 0) {
    ids.push(hintIdValue.value);
  }
  if (ids.length > 0) {
    return ids.join(' ');
  }
  return undefined;
});

const charCountText = computed(() => {
  const max = maxLengthValue.value;
  const current = modelValueString.value.length;
  if (max > 0) {
    return String(current) + '/' + String(max);
  }
  return String(current);
});

const sanitizeText = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  let v = value;
  v = v.replace(/\u0000/g, '');
  v = v.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
  if (props.sanitizeMode === 'text') {
    v = v.replace(/[<>]/g, '');
  }
  if (maxLengthValue.value > 0 && v.length > maxLengthValue.value) {
    v = v.slice(0, maxLengthValue.value);
  }
  return v;
};

const onInput = (e) => {
  const raw = e && e.target ? e.target.value : '';
  const next = props.sanitizeMode === 'none' ? raw : sanitizeText(raw);
  emit('update:modelValue', next);
  emit('input', next);
};
</script>


